import { EditorialPageLayout } from '@/components/seo/EditorialPageLayout';

export default function AffirmationsAndEmotionalWellness() {
  return (
    <EditorialPageLayout
      path="/affirmations-and-emotional-wellness"
      ogImage="/og/affirmations-and-emotional-wellness.jpg"
      title="Affirmations and Emotional Wellness — Say It NOW"
      metaDescription="Affirmations work when they're specific, honest, and received. A grounded look at how affirmations support emotional wellness — for the person speaking them and the person being told."
      eyebrow="The inner half"
      h1="Affirmations and emotional wellness."
      lede="Being told, in clear words, that you matter — and saying it to yourself when no one else is around — is one of the quieter forms of mental health care we have."
      articleSchema={{
        headline: 'Affirmations and emotional wellness',
        description:
          'A grounded look at how affirmations support emotional wellness — for the person being told and the person doing the telling — and why specificity is what makes them work.',
      }}
      related={[
        { label: 'Digital gratitude', href: '/digital-gratitude', hint: 'The outward half of the same practice.' },
        { label: 'Why Say It Now exists', href: '/why-say-it-now-exists', hint: 'The reason we built this in the first place.' },
        { label: 'What is a Living Announcement?', href: '/what-is-a-living-announcement', hint: 'How an affirmation becomes a keepsake.' },
      ]}
    >
      <p>
        Affirmations have a bad reputation, mostly because the loudest version
        of them is the worst version of them — "I am abundant, I am
        magnetic" repeated into a mirror until the words lose meaning. That
        is not what we mean here.
      </p>

      <p>
        A working affirmation is closer to a sentence a careful friend would
        say to you on a hard day. Specific. True. Slightly inconvenient to
        argue with. The kind of thing that lands because it is correct, not
        because it is loud.
      </p>

      <h2>What makes an affirmation actually work</h2>
      <ul>
        <li>
          <strong>It is specific.</strong> "You are a good parent" is okay.
          "The way you waited for him to finish the sentence is the kind of
          patience that becomes a person's whole foundation" is real.
        </li>
        <li>
          <strong>It is believable.</strong> If you do not believe it, it
          will not land. The work is in finding something true enough that
          you do.
        </li>
        <li>
          <strong>It is received.</strong> An affirmation said into the air
          is a thought. An affirmation given to a person becomes evidence
          they can hold.
        </li>
      </ul>

      <h2>The two directions</h2>
      <p>
        Affirmations work in both directions, and both directions support
        emotional wellness.
      </p>

      <h3>Outward — to someone else</h3>
      <p>
        Telling someone, in clear and specific language, what they are good
        at and what they have meant to you, is one of the most stable forms
        of love we know how to deliver. It outlasts the moment. People
        return to it.
      </p>

      <h3>Inward — to yourself</h3>
      <p>
        Writing down something true about yourself — and being able to come
        back to it on a worse day — is a small but durable act of self-care.
        Not because it changes reality. Because it gives you a sentence to
        stand on when reality gets loud.
      </p>

      <h2>Why specificity is the whole game</h2>
      <p>
        Generic affirmations bounce off. Specific ones embed. A nurse who is
        told "you are appreciated" hears nothing new. A nurse who is told
        "the way you sat with him for those last six hours is something his
        whole family will be telling stories about" hears something they can
        hold for the rest of their career.
      </p>

      <blockquote>
        Celebrate people while they're alive. Including yourself. Especially
        on the days that is hardest.
      </blockquote>

      <h2>A small practice you can start today</h2>
      <ol>
        <li>Write one specific, true sentence about someone you love.</li>
        <li>
          Write one specific, true sentence about yourself — about something
          you actually did this week.
        </li>
        <li>
          Send the first. Keep the second somewhere you can find it on a
          hard day.
        </li>
      </ol>

      <p>
        Both are forms of <a href="/digital-gratitude">digital gratitude</a>.
        Both are forms of mental health care. Both are quieter than they
        sound, and more powerful than they look.
      </p>
    </EditorialPageLayout>
  );
}
